import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { listProjects } from "@/api/projects";
import { useProjectStore } from "@/stores/project.store";
import { usePermissionDrift } from "@/hooks/usePermissionDrift";

/**
 * Keeps `currentProject` in sync with the `:slug` URL param.
 * Fetches the project list if empty, then activates the one matching the slug.
 */
export function ProjectSync() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const projects = useProjectStore((s) => s.projects);
  const setProjects = useProjectStore((s) => s.setProjects);
  const currentProject = useProjectStore((s) => s.currentProject);
  const setCurrentBySlug = useProjectStore((s) => s.setCurrentBySlug);

  // Dev-only: warn when this app gates on a permission key the backend
  // catalog no longer serves. Mounted here because it is the one place
  // every project route passes through exactly once. No-op in prod.
  usePermissionDrift(slug);

  useEffect(() => {
    if (!slug) return;

    if (projects.length > 0) {
      const match = setCurrentBySlug(slug);
      if (!match) {
        toast.error(`Project "${slug}" not found.`);
        navigate("/projects", { replace: true });
      }
      return;
    }

    listProjects()
      .then((data) => {
        setProjects(data);
        const match = data.find((p) => p.slug === slug);
        if (!match) {
          toast.error(`Project "${slug}" not found.`);
          navigate("/projects", { replace: true });
        } else {
          setCurrentBySlug(slug);
        }
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 401) return;
        toast.error("Couldn't load project.");
        navigate("/projects", { replace: true });
      });
  }, [
    slug,
    projects.length,
    setProjects,
    setCurrentBySlug,
    navigate,
    currentProject?.slug,
  ]);

  return null;
}
